import React, { useState, useEffect } from "react";
import styled from "styled-components";
import apiService from "../../../services/api";
import { required, email, cpf, phone } from "../../../utils/validation";
import { formatCPF, formatPhone, formatCEP } from "../../../utils/formatters";

const FormContainer = styled.div`
  max-width: 800px;
  background-color: #FFFFFF;
  border-radius: 11px;
  margin: 20px;
  padding: 0 24px 24px 24px;
  box-sizing: border-box;
  gap: 16px;
  overflow-y: auto;
`;

const FormStyled = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const Label = styled.label`
  font-size: 14px; 
  margin-bottom: 6px;
  font-weight: ${props => props.bold ? "bold" : "normal"};
  gap: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${props => props.hasError ? "#d00" : "#ccc"};
  border-radius: 9px;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: ${props => props.hasError ? "2px" : "6px"};

  &:focus {
    border-color: ${props => props.hasError ? "#d00" : "#89c2a1"};
    outline: none;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${props => props.hasError ? "#d00" : "#ccc"};
  border-radius: 9px;
  font-size: 14px;
  background-color: #fff;
  cursor: pointer;
  margin-bottom: ${props => props.hasError ? "2px" : "6px"};
`;

const Button = styled.button`
  padding: 10px 24px;
  background-color: #6AB8A8;
  color: white;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  font-size: 16px;
  margin: 0;
  align-self: flex-end;
  min-width: 120px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.223);

  &:hover {
    background-color: #56a998ff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FieldError = styled.div`
  color: #d00;
  font-size: 11px;
  margin-bottom: 4px;
`;

const ErrorText = styled.div`
  color: #d00;
  margin-top: 4px;
  font-size: 12px;
`;

const SuccessText = styled.div`
  color: green;
  margin-top: 4px;
  font-size: 13px;
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin: 20px 0;
`;

const VoltarButton = styled.button`
  background-color: #999;
  color: #ffffff;
  border: none;
  padding: 8px 24px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0px 4px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;

  &:hover {
    background-color: #888;
  }
`;

const isJwt = (t) => /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(t);
const baseUrl = process.env.REACT_APP_API_BASE_URL;

const fieldValidators = {
  curso: [required],
  areaCursoId: [required],
  campusId: [required],
  endereco: [required],
  numero: [required],
  bairro: [required],
  cidadeId: [required],
  estado: [required],
  cep: [required],
  coordenadorCurso: [required],
  cpfCoordenadorCurso: [required, cpf],
  telefone1CoordenadorCurso: [required, phone],
  telefone2CoordenadorCurso: [phone],
  emailCoordenadorCurso: [required, email],
  coordenadorEstagio: [required],
  cpfCoordenadorEstagio: [required, cpf],
  telefoneCoordenadorEstagio: [required, phone],
  telefone2CoordenadorEstagio: [phone],
  emailCoordenadorEstagio: [required, email],
};

const validateField = (name, value) => {
  const validators = fieldValidators[name] || [];
  for (const validator of validators) {
    const error = validator(value);
    if (error) return error;
  }
  return "";
};

const validateAll = (form) => {
  const errors = {};
  let hasErrors = false;
  for (const name of Object.keys(fieldValidators)) {
    const error = validateField(name, form[name]);
    if (error) {
      errors[name] = error;
      hasErrors = true;
    }
  }
  return { errors, hasErrors };
};

const formattedFields = {
  cpfCoordenadorCurso: formatCPF,
  cpfCoordenadorEstagio: formatCPF,
  telefone1CoordenadorCurso: formatPhone,
  telefone2CoordenadorCurso: formatPhone,
  telefoneCoordenadorEstagio: formatPhone,
  telefone2CoordenadorEstagio: formatPhone,
  cep: formatCEP,
};

const CursoForm = ({ editData = null, user = null, onVoltar }) => {
  const [form, setForm] = useState({
    curso: "",
    areaCursoId: "",
    campusId: "",
    endereco: "",
    numero: "",
    bairro: "",
    cidadeId: "",
    estado: "",
    cep: "",
    coordenadorCurso: "",
    cpfCoordenadorCurso: "",
    telefone1CoordenadorCurso: "",
    telefone2CoordenadorCurso: "",
    emailCoordenadorCurso: "",
    coordenadorEstagio: "",
    cpfCoordenadorEstagio: "",
    telefoneCoordenadorEstagio: "",
    telefone2CoordenadorEstagio: "",
    emailCoordenadorEstagio: "",
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [campis, setCampis] = useState([]);
  const [areaCursos, setAreaCursos] = useState([]);
  const [loadingCampis, setLoadingCampis] = useState(true);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const areasData = await apiService.getAreaCursos();
        setAreaCursos(areasData?.data ?? areasData ?? []);

        if (!user && !editData?.campisId) return;
        setLoadingCampis(true);
        let campisData = [];

        if (user?.cargoId === 1) {
          const allCampis = await apiService.getCampi();
          campisData = allCampis?.data ?? allCampis ?? [];
        } else if (user) {
          const myCampis = await apiService.getMyCampi();
          campisData = myCampis?.data ?? myCampis ?? [];
        }

        if ((!campisData || campisData.length === 0) && editData?.campisId) {
          const campusById = await apiService.getCampusById(editData.campisId);
          if (campusById) campisData = [campusById];
        }

        setCampis(campisData || []);
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error);
        setError('Erro ao carregar dependências do formulário.');
      } finally {
        setLoadingCampis(false);
      }
    };

    loadInitialData();
  }, [user, editData]);

  useEffect(() => {
    if (editData) {
      setForm({
        curso: editData.curso || "",
        areaCursoId: (editData.areaCursoId ?? editData.areaCurso?.id)?.toString() || "",
        campusId: editData.campusId?.toString() || editData.campisId?.toString() || "",
        endereco: editData.endereco || "",
        numero: editData.numero || "",
        bairro: editData.bairro || "",
        cidadeId: editData.cidadeId?.toString() || "",
        estado: editData.estado || "",
        cep: editData.cep || "",
        coordenadorCurso: editData.coordenadorCurso || "",
        cpfCoordenadorCurso: editData.cpfCoordenadorCurso || "",
        telefone1CoordenadorCurso: editData.telefone1CoordenadorCurso || "",
        telefone2CoordenadorCurso: editData.telefone2CoordenadorCurso || "",
        emailCoordenadorCurso: editData.emailCoordenadorCurso || "",
        coordenadorEstagio: editData.coordenadorEstagio || "",
        cpfCoordenadorEstagio: editData.cpfCoordenadorEstagio || "",
        telefoneCoordenadorEstagio: editData.telefoneCoordenadorEstagio || "",
        telefone2CoordenadorEstagio: editData.telefone2CoordenadorEstagio || "",
        emailCoordenadorEstagio: editData.emailCoordenadorEstagio || "",
      });
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const formatter = formattedFields[name];
    const formattedValue = formatter ? formatter(value) : value;

    if (name === 'campusId' && value) {
      const selectedCampus = campis.find(campus => campus.id.toString() === value);
      if (selectedCampus) {
        const updatedForm = {
          ...form,
          campusId: value,
          endereco: selectedCampus.endereco,
          numero: selectedCampus.numero,
          bairro: selectedCampus.bairro,
          cep: selectedCampus.cep,
          cidadeId: selectedCampus.cidadeId?.toString() || "",
          estado: selectedCampus.estado,
        };
        setForm(updatedForm);
        setFieldErrors(prev => ({ ...prev, campusId: validateField('campusId', value) }));
        return;
      }
    }

    setForm(prev => ({ ...prev, [name]: formattedValue }));

    if (touched[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: validateField(name, formattedValue) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setFieldErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const allTouched = Object.keys(fieldValidators).reduce((acc, key) => ({ ...acc, [key]: true }), {});
    setTouched(allTouched);

    const { errors, hasErrors } = validateAll(form);
    setFieldErrors(errors);

    if (hasErrors) {
      setError("Por favor, corrija os erros antes de salvar.");
      return;
    }

    setLoading(true);

    const stripDigits = (value) => value.replace(/\D/g, '');

    const payload = {
      ...form,
      campusId: Number(form.campusId),
      cidadeId: Number(form.cidadeId),
      areaCursoId: Number(form.areaCursoId),
      cep: formatCEP(form.cep),
      cpfCoordenadorCurso: stripDigits(form.cpfCoordenadorCurso),
      cpfCoordenadorEstagio: stripDigits(form.cpfCoordenadorEstagio),
      telefone1CoordenadorCurso: stripDigits(form.telefone1CoordenadorCurso),
      telefone2CoordenadorCurso: stripDigits(form.telefone2CoordenadorCurso),
      telefoneCoordenadorEstagio: stripDigits(form.telefoneCoordenadorEstagio),
      telefone2CoordenadorEstagio: stripDigits(form.telefone2CoordenadorEstagio),
      status: "ATIVO"
    };

    try {
      const token = localStorage.getItem("token");
      if (!token || !isJwt(token)) throw new Error("Erro de autenticação.");

      const isEditing = editData && editData.id;
      const url = isEditing ? `${baseUrl}/cursos/${editData.id}` : `${baseUrl}/cursos`;
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Erro: ${response.status}`);

      setSuccess(isEditing ? "Curso atualizado!" : "Curso cadastrado!");
      setTimeout(() => { window.location.href = "/Cursos"; }, 1500);

    } catch (error) {
      setError(error.message || "Erro ao salvar curso.");
    } finally {
      setLoading(false);
    }
  };

  const isEditing = editData && editData.id;

  const getError = (name) => touched[name] ? fieldErrors[name] : "";

  return (
    <FormContainer>
      <FormStyled onSubmit={handleSubmit}>
        <Row>
          <div style={{ flex: 1 }}>
            <Label bold>Curso*</Label>
            <Input
              type="text"
              name="curso"
              value={form.curso}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Ex: Enfermagem Noturno"
              hasError={!!getError("curso")}
            />
            {getError("curso") && <FieldError>{getError("curso")}</FieldError>}
          </div>
          <div style={{ flex: 1 }}>
            <Label bold>Área do Curso*</Label>
            <Select
              name="areaCursoId"
              value={form.areaCursoId}
              onChange={handleChange}
              onBlur={handleBlur}
              hasError={!!getError("areaCursoId")}
            >
              <option value="">Selecione a área</option>
              {areaCursos.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.nome}
                </option>
              ))}
            </Select>
            {getError("areaCursoId") && <FieldError>{getError("areaCursoId")}</FieldError>}
          </div>
        </Row>

        <div>
          <Label>Campus*</Label>
          <Select
            name="campusId"
            value={form.campusId}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={loadingCampis}
            hasError={!!getError("campusId")}
          >
            <option value="">
              {loadingCampis ? "Carregando campis..." : "Selecione um campus"}
            </option>
            {campis.map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.campus} - {campus.cidade}, {campus.estado}
              </option>
            ))}
          </Select>
          {getError("campusId") && <FieldError>{getError("campusId")}</FieldError>}
        </div>

        <Row>
          <div style={{ flex: 2 }}>
            <Label>Endereço*</Label>
            <Input type="text" name="endereco" value={form.endereco} onChange={handleChange} onBlur={handleBlur} hasError={!!getError("endereco")} />
            {getError("endereco") && <FieldError>{getError("endereco")}</FieldError>}
          </div>
          <div style={{ flex: 1 }}>
            <Label>Número*</Label>
            <Input type="text" name="numero" value={form.numero} onChange={handleChange} onBlur={handleBlur} hasError={!!getError("numero")} />
            {getError("numero") && <FieldError>{getError("numero")}</FieldError>}
          </div>
        </Row>

        <Row>
          <div style={{ flex: 1 }}>
            <Label>Bairro*</Label>
            <Input type="text" name="bairro" value={form.bairro} onChange={handleChange} onBlur={handleBlur} hasError={!!getError("bairro")} />
            {getError("bairro") && <FieldError>{getError("bairro")}</FieldError>}
          </div>
          <div style={{ flex: 1 }}>
            <Label>Estado*</Label>
            <Select name="estado" value={form.estado} onChange={handleChange} onBlur={handleBlur} hasError={!!getError("estado")}>
              <option value="">Selecione</option>
              {["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"].map(uf => (
                <option key={uf} value={uf}>{uf}</option>
              ))}
            </Select>
            {getError("estado") && <FieldError>{getError("estado")}</FieldError>}
          </div>
        </Row>

        <div>
          <Label>CEP*</Label>
          <Input type="text" name="cep" value={form.cep} onChange={handleChange} onBlur={handleBlur} hasError={!!getError("cep")} />
          {getError("cep") && <FieldError>{getError("cep")}</FieldError>}
        </div>

        <Row>
          <div style={{ flex: 2 }}>
            <Label bold>Coordenador do curso*</Label>
            <Input type="text" name="coordenadorCurso" value={form.coordenadorCurso} onChange={handleChange} onBlur={handleBlur} hasError={!!getError("coordenadorCurso")} />
            {getError("coordenadorCurso") && <FieldError>{getError("coordenadorCurso")}</FieldError>}
          </div>
          <div style={{ flex: 1 }}>
            <Label>CPF*</Label>
            <Input type="text" name="cpfCoordenadorCurso" value={form.cpfCoordenadorCurso} onChange={handleChange} onBlur={handleBlur} placeholder="000.000.000-00" hasError={!!getError("cpfCoordenadorCurso")} />
            {getError("cpfCoordenadorCurso") && <FieldError>{getError("cpfCoordenadorCurso")}</FieldError>}
          </div>
        </Row>

        <Row>
          <div style={{ flex: 1 }}>
            <Label>Telefone 1*</Label>
            <Input type="text" name="telefone1CoordenadorCurso" value={form.telefone1CoordenadorCurso} onChange={handleChange} onBlur={handleBlur} placeholder="(00) 00000-0000" hasError={!!getError("telefone1CoordenadorCurso")} />
            {getError("telefone1CoordenadorCurso") && <FieldError>{getError("telefone1CoordenadorCurso")}</FieldError>}
          </div>
          <div style={{ flex: 1 }}>
            <Label>Telefone 2</Label>
            <Input type="text" name="telefone2CoordenadorCurso" value={form.telefone2CoordenadorCurso} onChange={handleChange} onBlur={handleBlur} placeholder="(00) 00000-0000" hasError={!!getError("telefone2CoordenadorCurso")} />
            {getError("telefone2CoordenadorCurso") && <FieldError>{getError("telefone2CoordenadorCurso")}</FieldError>}
          </div>
        </Row>

        <div>
          <Label>Email do coordenador do curso*</Label>
          <Input type="email" name="emailCoordenadorCurso" value={form.emailCoordenadorCurso} onChange={handleChange} onBlur={handleBlur} hasError={!!getError("emailCoordenadorCurso")} />
          {getError("emailCoordenadorCurso") && <FieldError>{getError("emailCoordenadorCurso")}</FieldError>}
        </div>

        <Row>
          <div style={{ flex: 2 }}>
            <Label bold>Coordenador do estágio*</Label>
            <Input type="text" name="coordenadorEstagio" value={form.coordenadorEstagio} onChange={handleChange} onBlur={handleBlur} hasError={!!getError("coordenadorEstagio")} />
            {getError("coordenadorEstagio") && <FieldError>{getError("coordenadorEstagio")}</FieldError>}
          </div>
          <div style={{ flex: 1 }}>
            <Label>CPF*</Label>
            <Input type="text" name="cpfCoordenadorEstagio" value={form.cpfCoordenadorEstagio} onChange={handleChange} onBlur={handleBlur} placeholder="000.000.000-00" hasError={!!getError("cpfCoordenadorEstagio")} />
            {getError("cpfCoordenadorEstagio") && <FieldError>{getError("cpfCoordenadorEstagio")}</FieldError>}
          </div>
        </Row>

        <Row>
          <div style={{ flex: 1 }}>
            <Label>Telefone 1*</Label>
            <Input type="text" name="telefoneCoordenadorEstagio" value={form.telefoneCoordenadorEstagio} onChange={handleChange} onBlur={handleBlur} placeholder="(00) 00000-0000" hasError={!!getError("telefoneCoordenadorEstagio")} />
            {getError("telefoneCoordenadorEstagio") && <FieldError>{getError("telefoneCoordenadorEstagio")}</FieldError>}
          </div>
          <div style={{ flex: 1 }}>
            <Label>Telefone 2</Label>
            <Input type="text" name="telefone2CoordenadorEstagio" value={form.telefone2CoordenadorEstagio} onChange={handleChange} onBlur={handleBlur} placeholder="(00) 00000-0000" hasError={!!getError("telefone2CoordenadorEstagio")} />
            {getError("telefone2CoordenadorEstagio") && <FieldError>{getError("telefone2CoordenadorEstagio")}</FieldError>}
          </div>
        </Row>

        <div>
          <Label>Email do coordenador de estágio*</Label>
          <Input type="email" name="emailCoordenadorEstagio" value={form.emailCoordenadorEstagio} onChange={handleChange} onBlur={handleBlur} hasError={!!getError("emailCoordenadorEstagio")} />
          {getError("emailCoordenadorEstagio") && <FieldError>{getError("emailCoordenadorEstagio")}</FieldError>}
        </div>

        <MessageContainer>
          {error && <ErrorText>{error}</ErrorText>}
          {success && <SuccessText>{success}</SuccessText>}
        </MessageContainer>

        <ActionsRow>
          {onVoltar ? (
            <VoltarButton type="button" onClick={onVoltar} disabled={loading}>
              Voltar
            </VoltarButton>
          ) : (
            <span />
          )}
          <Button type="submit" disabled={loading}>
            {loading ? "Salvando..." : isEditing ? "Atualizar" : "Salvar"}
          </Button>
        </ActionsRow>
      </FormStyled>
    </FormContainer>
  );
};

export default CursoForm;
